import React from "react";
import PropTypes from "prop-types";
import InfoIcon from "../InfoIcon";
import { typography, textColors, surfaces, borders } from "../../../design-system/tokens";

// Correct compass angles for SVG (0°=right, 90°=up, 180°=left, 270°=down)
const DIRECTIONS = [
  { key: "right", angle: 90, label: "→" },
  { key: "top-right", angle: 45, label: "↗" },
  { key: "top", angle: 0, label: "↑" },
  { key: "top-left", angle: 315, label: "↖" },
  { key: "left", angle: 270, label: "←" },
  { key: "bottom-left", angle: 225, label: "↙" },
  { key: "bottom", angle: 180, label: "↓" },
  { key: "bottom-right", angle: 135, label: "↘" },
  { key: "center", angle: null, label: "•" },
];

function polarToCartesian(cx, cy, r, angle) {
  const rad = (angle - 90) * (Math.PI / 180);
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

export function DirectionsField({ label, help, value, onChange, infoText }) {
  // Accept value as comma-separated string or array
  const selected = Array.isArray(value)
    ? value
    : (value || "").split(",").map((v) => v.trim()).filter(Boolean);

  const toggleDirection = (dir) => {
    let newSelected;
    if (selected.includes(dir)) {
      newSelected = selected.filter((d) => d !== dir);
    } else {
      newSelected = [...selected, dir];
    }
    onChange(newSelected.join(","));
  };

  // SVG layout — larger for better UX
  const size = 160;
  const center = size / 2;
  const radius = 54;
  const nodeRadius = 16;
  const isCenterSelected = selected.includes("center");

  return (
    <div className="field-wrapper">
      <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
        {label}
        {infoText ? <span className="ml-2 align-middle"><InfoIcon text={infoText} /></span> : null}
      </label>
      {help && <div className={`mb-3 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}

      <div className={`flex flex-col items-center p-4 rounded-xl border ${borders.default} ${surfaces.secondary}`}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ display: "block" }}
        >
          {/* Background ring */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            className="fill-none stroke-gray-200 dark:stroke-gray-700"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />

          {/* Tick marks connecting center to edges */}
          {DIRECTIONS.filter((d) => d.key !== "center").map((dir) => {
            const inner = polarToCartesian(center, center, nodeRadius + 2, dir.angle);
            const outer = polarToCartesian(center, center, radius - nodeRadius - 2, dir.angle);
            return (
              <line
                key={`tick-${dir.key}`}
                x1={inner.x}
                y1={inner.y}
                x2={outer.x}
                y2={outer.y}
                className="stroke-gray-200 dark:stroke-gray-700"
                strokeWidth="1"
              />
            );
          })}

          {/* Direction nodes */}
          {DIRECTIONS.filter((d) => d.key !== "center").map((dir) => {
            const { x, y } = polarToCartesian(center, center, radius, dir.angle);
            const isSelected = selected.includes(dir.key);
            return (
              <g
                key={dir.key}
                onClick={() => toggleDirection(dir.key)}
                style={{ cursor: "pointer" }}
                role="button"
                aria-label={`Toggle ${dir.key} direction`}
                aria-pressed={isSelected}
              >
                <circle
                  cx={x}
                  cy={y}
                  r={nodeRadius}
                  className={
                    isSelected
                      ? "fill-blue-600 dark:fill-blue-500 stroke-blue-700 dark:stroke-blue-400"
                      : "fill-white dark:fill-gray-800 stroke-gray-300 dark:stroke-gray-600"
                  }
                  strokeWidth={isSelected ? 2 : 1.5}
                />
                <text
                  x={x}
                  y={y + 5}
                  textAnchor="middle"
                  fontSize="16"
                  className={isSelected ? "fill-white" : "fill-gray-600 dark:fill-gray-300"}
                  pointerEvents="none"
                  style={{ userSelect: "none" }}
                >
                  {dir.label}
                </text>
              </g>
            );
          })}

          {/* Center node */}
          <g
            onClick={() => toggleDirection("center")}
            style={{ cursor: "pointer" }}
            role="button"
            aria-label="Toggle center direction"
            aria-pressed={isCenterSelected}
          >
            <circle
              cx={center}
              cy={center}
              r={nodeRadius}
              className={
                isCenterSelected
                  ? "fill-blue-600 dark:fill-blue-500 stroke-blue-700 dark:stroke-blue-400"
                  : "fill-white dark:fill-gray-800 stroke-gray-300 dark:stroke-gray-600"
              }
              strokeWidth={isCenterSelected ? 2 : 1.5}
            />
            <text
              x={center}
              y={center + 6}
              textAnchor="middle"
              fontSize="18"
              className={isCenterSelected ? "fill-white" : "fill-gray-600 dark:fill-gray-300"}
              pointerEvents="none"
              style={{ userSelect: "none" }}
            >
              •
            </text>
          </g>
        </svg>

        {/* Selection summary */}
        <div className={`mt-3 ${typography.caption} ${textColors.tertiary} text-center`}>
          {selected.length > 0 ? (
            <span>
              Selected:{" "}
              {selected.map((dir, i) => (
                <span key={dir}>
                  {i > 0 && ", "}
                  <span className={`${textColors.primary} font-medium`}>{dir}</span>
                </span>
              ))}
            </span>
          ) : (
            "None selected — defaults to center"
          )}
        </div>
      </div>
    </div>
  );
}

DirectionsField.propTypes = {
  label: PropTypes.string,
  help: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  infoText: PropTypes.string,
};

export default DirectionsField;
